import { useEffect, useMemo, useState } from "react";
import {
    FileClock, Shield, User as UserIcon, Loader2, RefreshCw, Filter,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/* ============================================================================
   SİSTEM > AYARLAR > LOG
   ----------------------------------------------------------------------------
   Hesap sahibine özel. Backend'de owner-only (require_owner). Her yazma
   işlemi burada okunabilir satır olarak akar. Süper admin "panele giriş
   yaptı" türünden eylemler ayrı renk + 🛡️ ikon ile ayırt edilir.
============================================================================ */

const RANGE_OPTIONS = [
    { key: "today", label: "Bugün" },
    { key: "7d",    label: "Son 7 gün" },
    { key: "30d",   label: "Son 30 gün" },
    { key: "all",   label: "Tümü" },
];

const ACTOR_OPTIONS = [
    { key: "all",   label: "Herkes" },
    { key: "user",  label: "Ekip Üyeleri" },
    { key: "admin", label: "🛡️ Sayz Ekibi" },
];

const CATEGORY_OPTIONS = [
    { key: "all",         label: "Tüm Kategoriler" },
    { key: "team",        label: "Ekip" },
    { key: "profile",     label: "Profil" },
    { key: "tenant",      label: "Hesap" },
    { key: "site",        label: "Mağaza" },
    { key: "series",      label: "Beden Serisi" },
    { key: "group",       label: "Ölçü Grubu" },
    { key: "model",       label: "Model" },
    { key: "table",       label: "Beden Tablosu" },
    { key: "product",     label: "Ürün" },
    { key: "widget",      label: "Widget" },
    { key: "fit_labels",  label: "Kalıp Etiketleri" },
];

const PAGE_SIZE = 50;


function formatDateTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        // DD MMM YYYY · HH:mm:ss (TR)
        const date = d.toLocaleDateString("tr-TR", {
            day: "2-digit", month: "short", year: "numeric",
        });
        const time = d.toLocaleTimeString("tr-TR", {
            hour: "2-digit", minute: "2-digit", second: "2-digit",
        });
        return `${date} · ${time}`;
    } catch {
        return iso;
    }
}

function initials(name = "") {
    return (
        name.split(" ").filter(Boolean).slice(0, 2).map(s => s[0]).join("").toUpperCase() || "?"
    );
}


function LogRow({ item }) {
    const isAdmin = item.actor_kind === "admin";

    // Admin satırları — mor arkaplan + kalkan
    if (isAdmin) {
        return (
            <li
                data-testid="audit-row-admin"
                className="group relative flex items-start gap-3 rounded-lg border border-violet-200 bg-violet-50/60 px-4 py-3 transition-colors hover:bg-violet-50"
            >
                <div className="shrink-0 h-10 w-10 rounded-full bg-violet-100 text-violet-700 grid place-items-center ring-2 ring-violet-200">
                    <Shield className="h-4 w-4" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-semibold text-sm text-violet-900">
                            Sayz tarafından
                        </span>
                        <Badge
                            variant="outline"
                            className="text-[10px] uppercase tracking-wider border-violet-300 text-violet-700 bg-violet-100/60 h-5 px-1.5"
                        >
                            Yetkili Erişim
                        </Badge>
                    </div>
                    <div className="text-sm text-foreground mt-1 leading-relaxed">
                        <span className="text-muted-foreground">·</span>{" "}
                        <span>{item.verb}</span>
                        {item.target_name && (
                            <>
                                {" "}→{" "}
                                <code className="text-[11px] bg-background border border-violet-200 px-1.5 py-0.5 rounded font-mono text-violet-900">
                                    {item.target_name}
                                </code>
                            </>
                        )}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono mt-1.5">
                        {formatDateTime(item.created_at)}
                        {item.ip ? ` · IP: ${item.ip}` : ""}
                    </div>
                </div>
                <Badge variant="outline" className="text-[10px] shrink-0 mt-0.5">
                    {item.category}
                </Badge>
            </li>
        );
    }

    // Normal kullanıcı satırları
    return (
        <li
            data-testid="audit-row-user"
            className="group relative flex items-start gap-3 rounded-lg border border-border bg-surface px-4 py-3 transition-colors hover:bg-muted/40"
        >
            <Avatar className="shrink-0 h-10 w-10 border border-border">
                <AvatarFallback className="bg-foreground text-background font-display font-bold text-xs">
                    {initials(item.actor_label)}
                </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0 pt-0.5">
                <div className="text-sm leading-relaxed">
                    <span className="font-semibold">{item.actor_label}</span>{" "}
                    <span className="text-muted-foreground">·</span>{" "}
                    <span className="text-foreground">{item.verb}</span>
                    {item.target_name && (
                        <>
                            {" "}→{" "}
                            <code className="text-[11px] bg-muted border border-border px-1.5 py-0.5 rounded font-mono text-foreground">
                                {item.target_name}
                            </code>
                        </>
                    )}
                    {item.meta?.count ? (
                        <span className="text-muted-foreground"> · {item.meta.count} kayıt</span>
                    ) : null}
                </div>
                <div className="text-[11px] text-muted-foreground font-mono mt-1.5">
                    {formatDateTime(item.created_at)}
                    {item.ip ? ` · IP: ${item.ip}` : ""}
                </div>
            </div>
            <Badge variant="outline" className="text-[10px] shrink-0 mt-0.5">
                {item.category}
            </Badge>
        </li>
    );
}


export default function AuditLogTab() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [range, setRange] = useState("30d");
    const [actor, setActor] = useState("all");
    const [category, setCategory] = useState("all");

    const load = async (opts = {}) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("limit", String(PAGE_SIZE));
            params.set("skip", String((opts.page ?? page) * PAGE_SIZE));
            const rk = opts.range ?? range;
            if (rk && rk !== "all") params.set("rangeKey", rk);
            const ak = opts.actor ?? actor;
            if (ak && ak !== "all") params.set("actor", ak);
            const ck = opts.category ?? category;
            if (ck && ck !== "all") params.set("category", ck);
            const { data } = await api.get(`/audit/logs?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (e) {
            if (e?.response?.status === 403) {
                toast.error("Log'ları yalnızca hesap sahibi görüntüleyebilir.");
            } else {
                toast.error(e?.response?.data?.detail || "Log yüklenemedi");
            }
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load({ page: 0 }); }, []);

    const changeRange = (v) => {
        setRange(v);
        setPage(0);
        load({ range: v, page: 0 });
    };
    const changeActor = (v) => {
        setActor(v);
        setPage(0);
        load({ actor: v, page: 0 });
    };
    const changeCategory = (v) => {
        setCategory(v);
        setPage(0);
        load({ category: v, page: 0 });
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const hasAdmin = useMemo(() => items.some((i) => i.actor_kind === "admin"), [items]);

    return (
        <div className="space-y-5" data-testid="audit-log-tab">
            {/* Başlık + yardım */}
            <div className="rounded-xl border border-border bg-surface">
                <div className="px-5 pt-5 pb-3 border-b border-border flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-md bg-foreground text-background grid place-items-center shrink-0 mt-0.5">
                            <FileClock className="h-4 w-4" strokeWidth={1.75} />
                        </div>
                        <div>
                            <h3 className="font-display text-base font-semibold tracking-tight">
                                İşlem Kayıtları
                            </h3>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed max-w-xl">
                                Hesabında kim, ne zaman ve neyi değiştirdi — tüm veri etkisi
                                olan işlemler buraya saniyesine kadar düşer. Mor satırlar
                                <span className="mx-1 inline-flex items-center gap-1 text-violet-700 font-medium">
                                    <Shield className="h-3 w-3" /> Sayz ekibinin
                                </span>
                                yetkili erişim ile yaptığı işlemleri gösterir.
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => load()}
                        disabled={loading}
                        data-testid="audit-refresh-btn"
                        className="shrink-0 gap-1.5"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                        Yenile
                    </Button>
                </div>

                {/* Filtreler */}
                <div className="px-5 py-3 border-b border-border flex flex-wrap items-center gap-2 bg-muted/20">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
                        <Filter className="h-3 w-3" /> Filtre
                    </div>
                    <Select value={range} onValueChange={changeRange}>
                        <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="audit-filter-range">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {RANGE_OPTIONS.map((r) => (
                                <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={actor} onValueChange={changeActor}>
                        <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="audit-filter-actor">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {ACTOR_OPTIONS.map((r) => (
                                <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={category} onValueChange={changeCategory}>
                        <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="audit-filter-category">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {CATEGORY_OPTIONS.map((r) => (
                                <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <div className="ml-auto text-[11px] text-muted-foreground">
                        Toplam <span className="font-semibold text-foreground">{total}</span> işlem
                        {hasAdmin && (
                            <span className="ml-2 inline-flex items-center gap-1 text-violet-700">
                                <Shield className="h-3 w-3" /> Sayz izi var
                            </span>
                        )}
                    </div>
                </div>

                {/* Liste */}
                <div className="p-4">
                    {loading && items.length === 0 ? (
                        <div className="py-12 flex flex-col items-center justify-center text-muted-foreground">
                            <Loader2 className="h-5 w-5 animate-spin mb-2" />
                            <p className="text-sm">Log yükleniyor…</p>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="py-12 flex flex-col items-center justify-center text-muted-foreground" data-testid="audit-empty">
                            <FileClock className="h-8 w-8 mb-2 opacity-40" strokeWidth={1.25} />
                            <p className="text-sm">Seçilen filtreyle eşleşen işlem bulunamadı.</p>
                        </div>
                    ) : (
                        <ul className="space-y-2" data-testid="audit-list">
                            {items.map((item) => (
                                <LogRow key={item.id} item={item} />
                            ))}
                        </ul>
                    )}
                </div>

                {/* Sayfalama */}
                {total > PAGE_SIZE && (
                    <div className="px-5 py-3 border-t border-border flex items-center justify-between bg-muted/10">
                        <div className="text-xs text-muted-foreground">
                            Sayfa <span className="font-semibold text-foreground">{page + 1}</span> / {totalPages}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page === 0 || loading}
                                onClick={() => {
                                    const p = Math.max(0, page - 1);
                                    setPage(p); load({ page: p });
                                }}
                                data-testid="audit-page-prev"
                            >
                                Önceki
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page + 1 >= totalPages || loading}
                                onClick={() => {
                                    const p = page + 1;
                                    setPage(p); load({ page: p });
                                }}
                                data-testid="audit-page-next"
                            >
                                Sonraki
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
